/* eslint-disable import-extensions/file-extension-required */
import type { GraphQLError } from 'graphql';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expVal } from '@confluence/feature-experiments';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import FetchJiraMediaTokenQuery, {
	type graphqlHelpersFetchJiraMediaTokenQuery$data,
	type graphqlHelpersFetchJiraMediaTokenQuery$variables,
} from '../MediaProvider/__generated__/graphqlHelpersFetchJiraMediaTokenQuery.graphql';
import FetchMediaTokenQuery, {
	type graphqlHelpersFetchMediaTokenQuery$data,
	type graphqlHelpersFetchMediaTokenQuery$variables,
} from '../MediaProvider/__generated__/graphqlHelpersFetchMediaTokenQuery.graphql';
import FetchMediaTokenWithMediaItemsQuery, {
	type graphqlHelpersFetchMediaTokenWithMediaItemsQuery$data,
	type graphqlHelpersFetchMediaTokenWithMediaItemsQuery$variables,
} from '../MediaProvider/__generated__/graphqlHelpersFetchMediaTokenWithMediaItemsQuery.graphql';

type RouteOwnedMediaData = {
	mediaConfiguration?: {
		clientId?: string | null;
		fileStoreUrl?: string | null;
	} | null;
	content?: {
		nodes?: ReadonlyArray<{
			id?: string | null;
			body?: {
				atlas_doc_format?: {
					mediaToken?: {
						token?: string | null;
					} | null;
				} | null;
			} | null;
		} | null> | null;
	} | null;
};

/**
 * Seeds the media provider cache from route-owned data so the first renderer auth request does not
 * fetch the same token after the standalone route preloader is removed.
 */
export const primeMediaTokenFromRouteData = async (
	contentId: string,
	data: RouteOwnedMediaData | null | undefined,
): Promise<void> => {
	const mediaConfiguration = data?.mediaConfiguration
		? {
				clientId: data.mediaConfiguration.clientId || '',
				fileStoreUrl: data.mediaConfiguration.fileStoreUrl || '',
			}
		: null;
	const content = data?.content
		? {
				nodes:
					data.content.nodes?.map((node) =>
						node
							? {
									id: node.id || null,
									body: node.body
										? {
												atlas_doc_format: node.body.atlas_doc_format
													? {
															mediaToken: node.body.atlas_doc_format.mediaToken
																? { token: node.body.atlas_doc_format.mediaToken.token || null }
																: null,
														}
													: null,
											}
										: null,
								}
							: null,
					) || [],
			}
		: null;

	writeQueryInterop<
		graphqlHelpersFetchMediaTokenQuery$data,
		graphqlHelpersFetchMediaTokenQuery$variables
	>({
		query: FetchMediaTokenQuery,
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
		},
		data: { content, mediaConfiguration },
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { mediaConfigRecorder } = await import(
			/* webpackChunkName: 'loadable-confluencessr-utilities-objectrecorder' */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);
		mediaConfigRecorder.set({
			clientId: mediaConfiguration?.clientId || '',
			baseUrl: mediaConfiguration?.fileStoreUrl || '',
			token: content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
		});
	}
};

export const preloadJiraMediaToken = async (
	issueAri: string,
): Promise<{
	data: graphqlHelpersFetchJiraMediaTokenQuery$data | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> => {
	const response = await queryAgg<
		graphqlHelpersFetchJiraMediaTokenQuery$data,
		graphqlHelpersFetchJiraMediaTokenQuery$variables
	>({
		query: FetchJiraMediaTokenQuery,
		variables: { issueAri, maxTokenLength: 65536 },
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const mediaReadToken = response?.data?.jira?.issueById?.mediaReadToken;

		const { jiraMediaConfigRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);

		// Only record valid credentials. If clientId or endpointUrl are missing
		// (e.g. the @optIn backend directive is not enabled, or the issue has no
		// media), writing empty strings would produce a truthy object that bypasses
		// the `if (!recordedAuth)` guard in ssrMediaHelpers and causes the media
		// client to attempt rendering with invalid auth — skipping the set entirely
		// is the safe default so SSR falls back to client-side auth gracefully.
		const clientId = mediaReadToken?.clientId;
		const baseUrl = mediaReadToken?.endpointUrl;
		const token = mediaReadToken?.tokensWithFiles?.edges?.[0]?.node?.token;

		if (clientId && baseUrl) {
			jiraMediaConfigRecorder.set({
				clientId,
				baseUrl,
				token: token || '',
			});
		}
	}

	return response;
};

export const preloadMediaToken = async (
	contentId: string,
	cloudId: string,
): Promise<{
	data:
		| graphqlHelpersFetchMediaTokenQuery$data
		| graphqlHelpersFetchMediaTokenWithMediaItemsQuery$data
		| null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> => {
	if (expVal('confluence_media_token_preload_agg_query', 'isEnabled', false)) {
		const response = await query<
			graphqlHelpersFetchMediaTokenWithMediaItemsQuery$data,
			graphqlHelpersFetchMediaTokenWithMediaItemsQuery$variables
		>({
			query: FetchMediaTokenWithMediaItemsQuery,
			variables: {
				cloudId,
				contentId,
				status: ['current', 'draft', 'archived'],
				missingContentId: !contentId,
			},
		});

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			const { mediaConfigRecorder } = await import(
				/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
			);
			const { setMediaNodes } = await import(
				/* webpackChunkName: "loadable-atlaskit-media-common-medianodesrecorder" */ '@atlaskit/media-common/mediaNodesRecorder'
			);
			mediaConfigRecorder.set({
				clientId: response?.data?.mediaConfiguration?.clientId || '',
				baseUrl: response?.data?.mediaConfiguration?.fileStoreUrl || '',
				token: response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
			});

			// Record the aggregated mediaItems metadata so it can be injected onto
			// `window.__MEDIA_NODE_META_DATA__` during handleReactRender.
			const mediaItems = response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaItems;
			if (mediaItems) {
				setMediaNodes(mediaItems);
			}
		}

		return response;
	}

	const response = await query<
		graphqlHelpersFetchMediaTokenQuery$data,
		graphqlHelpersFetchMediaTokenQuery$variables
	>({
		query: FetchMediaTokenQuery,
		variables: {
			contentId,
			status: ['current', 'draft', 'archived'],
		},
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { mediaConfigRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);
		mediaConfigRecorder.set({
			clientId: response?.data?.mediaConfiguration?.clientId || '',
			baseUrl: response?.data?.mediaConfiguration?.fileStoreUrl || '',
			token: response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.mediaToken?.token || '',
		});
	}

	return response;
};
